// Parental Consent Verification Page
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Shield, CheckCircle, XCircle, AlertTriangle, Clock } from 'lucide-react';
import { ParentalConsentService } from '../lib/parentalConsentService';
import { ParentalConsent } from '../entities/ParentalConsent';

export const ParentalConsentPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(null);
  const [consent, setConsent] = useState<ParentalConsent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [approved, setApproved] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
      verifyToken(tokenParam);
    } else {
      setError('Invalid verification link');
      setLoading(false);
    }
  }, [searchParams]);

  const verifyToken = async (token: string) => {
    setLoading(true);
    try {
      const consentData = await ParentalConsentService.verifyConsent(token);
      if (consentData) {
        setConsent(consentData);
        
        if (consentData.consent_status === 'expired') {
          setError('This consent request has expired. Please contact support.');
        } else if (consentData.consent_status === 'approved') {
          setApproved(true);
        } else if (consentData.consent_status === 'denied') {
          setDenied(true);
        }
      } else {
        setError('Invalid or expired verification link');
      }
    } catch (err) {
      setError('Failed to verify consent request');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const success = await ParentalConsentService.approveConsent(
        token,
        // In production, get real IP and user agent
        '127.0.0.1',
        navigator.userAgent
      );
      
      if (success) {
        setApproved(true);
      } else {
        setError('Failed to approve consent');
      }
    } catch (err) {
      setError('An error occurred while approving consent');
    } finally {
      setLoading(false);
    }
  };

  const handleDeny = async () => {
    if (!token || !window.confirm('Are you sure you want to deny access? The account will not be activated.')) {
      return;
    }
    
    setLoading(true);
    try {
      const success = await ParentalConsentService.denyConsent(token);
      
      if (success) {
        setDenied(true);
      } else {
        setError('Failed to deny consent');
      }
    } catch (err) {
      setError('An error occurred while denying consent');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !consent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0118] via-[#1a0f2e] to-[#0a0118] flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Verifying consent request...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0118] via-[#1a0f2e] to-[#0a0118] flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-[#1a0f2e] to-[#0a0118] border-2 border-red-400/30 rounded-2xl w-full max-w-2xl p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Verification Failed</h1>
          <p className="text-red-300 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-semibold transition-all"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  if (approved) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0118] via-[#1a0f2e] to-[#0a0118] flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-[#1a0f2e] to-[#0a0118] border-2 border-green-400/30 rounded-2xl w-full max-w-2xl p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">✅ Consent Approved!</h1>
          <p className="text-green-300 mb-6">The child's account has been activated successfully.</p>
          
          <div className="rounded-lg bg-green-900/20 border border-green-400/30 p-6 text-left mb-6">
            <h3 className="text-lg font-semibold text-green-200 mb-3">What Happens Next:</h3>
            <ul className="text-sm text-green-300 space-y-2">
              <li>✅ The child ({consent?.child_email}) will receive an activation email</li>
              <li>✅ They can now log in and use age-appropriate features</li>
              <li>✅ Social features and messaging remain restricted until age 13</li>
              <li>✅ You'll receive monthly updates on their activity</li>
            </ul>
          </div>

          <div className="rounded-lg bg-blue-900/20 border border-blue-400/30 p-6 text-left mb-6">
            <h3 className="text-lg font-semibold text-blue-200 mb-3">📧 Parent Dashboard Access:</h3>
            <p className="text-sm text-blue-300 mb-3">
              We've sent you access to the Parent Dashboard where you can:
            </p>
            <ul className="text-sm text-blue-300 space-y-1 list-disc list-inside">
              <li>Monitor account activity</li>
              <li>Review and delete data</li>
              <li>Update consent preferences</li>
              <li>Revoke access anytime</li>
            </ul>
          </div>

          <button
            onClick={() => navigate('/')}
            className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-semibold transition-all"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  if (denied) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0118] via-[#1a0f2e] to-[#0a0118] flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-[#1a0f2e] to-[#0a0118] border-2 border-amber-400/30 rounded-2xl w-full max-w-2xl p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-10 h-10 text-amber-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-amber-300 mb-6">You've declined to grant consent for this account.</p>
          
          <div className="rounded-lg bg-amber-900/20 border border-amber-400/30 p-6 text-left mb-6">
            <p className="text-sm text-amber-300">
              The account will remain inactive and will be automatically deleted after 30 days. 
              The child will not be able to access the platform.
            </p>
          </div>

          <button
            onClick={() => navigate('/')}
            className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-semibold transition-all"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // Main consent review screen
  const childAge = consent ? ParentalConsentService.calculateAge(consent.child_birthdate) : 0;
  const expiresAt = consent ? new Date(consent.expires_at) : new Date();
  const daysLeft = Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0118] via-[#1a0f2e] to-[#0a0118] p-4 py-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-br from-[#1a0f2e] to-[#0a0118] border-2 border-purple-400/30 rounded-2xl p-8 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center">
              <Shield className="w-8 h-8 text-purple-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Parental Consent Required</h1>
              <p className="text-slate-400">COPPA Compliance - Review and approve child account</p>
            </div>
          </div>
          
          <div className="rounded-lg bg-amber-900/20 border border-amber-400/30 p-4 flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-200">Time Sensitive</p>
              <p className="text-xs text-amber-300">This consent request expires in <strong>{daysLeft} day{daysLeft !== 1 ? 's' : ''}</strong></p>
            </div>
          </div>
        </div>

        {/* Account Details */}
        <div className="bg-gradient-to-br from-[#1a0f2e] to-[#0a0118] border-2 border-blue-400/30 rounded-2xl p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">📋 Account Request Details</h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-white/10">
              <span className="text-slate-400">Child's Email:</span>
              <span className="text-white font-semibold">{consent?.child_email}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-white/10">
              <span className="text-slate-400">Child's Age:</span>
              <span className="text-white font-semibold">{childAge} years old</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-white/10">
              <span className="text-slate-400">Request Date:</span>
              <span className="text-white font-semibold">{consent && new Date(consent.created_at).toLocaleDateString()}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-slate-400">Parent Email:</span>
              <span className="text-white font-semibold">{consent?.parent_email}</span>
            </div>
          </div>
        </div>

        {/* Data Collection Info */}
        <div className="bg-gradient-to-br from-[#1a0f2e] to-[#0a0118] border-2 border-emerald-400/30 rounded-2xl p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">🔒 Privacy & Data Protection</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-emerald-200 mb-2">Data We Collect:</h3>
              <ul className="text-sm text-emerald-300 space-y-1 list-disc list-inside">
                <li>Email address (for login)</li>
                <li>Age/birthdate (for age verification)</li>
                <li>Written documents and assignments</li>
                <li>Usage data (feature usage, login times)</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-emerald-200 mb-2">We DO NOT Collect:</h3>
              <ul className="text-sm text-emerald-300 space-y-1 list-disc list-inside">
                <li>Location data or GPS coordinates</li>
                <li>Financial information</li>
                <li>Social security numbers</li>
                <li>Photos or videos (without explicit permission)</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-emerald-200 mb-2">Your Rights as a Parent:</h3>
              <ul className="text-sm text-emerald-300 space-y-1 list-disc list-inside">
                <li>Review all data collected about your child</li>
                <li>Request deletion of data at any time</li>
                <li>Revoke consent and close the account</li>
                <li>Receive monthly activity reports</li>
                <li>Contact us with questions or concerns</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Features & Restrictions */}
        <div className="bg-gradient-to-br from-[#1a0f2e] to-[#0a0118] border-2 border-amber-400/30 rounded-2xl p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">🎓 Features & Age Restrictions</h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-lg bg-green-900/20 border border-green-400/30 p-4">
              <h3 className="text-sm font-semibold text-green-200 mb-2">✅ Available Features:</h3>
              <ul className="text-xs text-green-300 space-y-1">
                <li>• Writing tools and templates</li>
                <li>• Citation management</li>
                <li>• Document storage</li>
                <li>• Study organization</li>
                <li>• Academic resources</li>
              </ul>
            </div>

            <div className="rounded-lg bg-red-900/20 border border-red-400/30 p-4">
              <h3 className="text-sm font-semibold text-red-200 mb-2">❌ Restricted (Until Age 13):</h3>
              <ul className="text-xs text-red-300 space-y-1">
                <li>• Direct messaging</li>
                <li>• Social features</li>
                <li>• Public profiles</li>
                <li>• Video chat rooms</li>
                <li>• Music player</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Legal Compliance */}
        <div className="bg-gradient-to-br from-[#1a0f2e] to-[#0a0118] border-2 border-blue-400/30 rounded-2xl p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">⚖️ Legal Compliance</h2>
          <div className="space-y-3 text-sm text-blue-300">
            <p><strong className="text-blue-200">COPPA Compliant:</strong> We comply with the Children's Online Privacy Protection Act (COPPA) for users under 13.</p>
            <p><strong className="text-blue-200">FERPA Compliant:</strong> We meet Family Educational Rights and Privacy Act (FERPA) standards for educational institutions.</p>
            <p><strong className="text-blue-200">Data Security:</strong> All data is encrypted in transit and at rest using industry-standard protocols.</p>
            <p><strong className="text-blue-200">Third-Party Sharing:</strong> We never sell or share your child's data with third parties for marketing purposes.</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-gradient-to-br from-[#1a0f2e] to-[#0a0118] border-2 border-purple-400/30 rounded-2xl p-8">
          <h2 className="text-xl font-bold text-white mb-4 text-center">Your Decision</h2>
          <p className="text-slate-300 text-center mb-6">
            By clicking "Approve & Grant Consent" below, you confirm that you are the parent or legal guardian 
            of the child requesting account access, and you consent to the collection and use of their information 
            as described in our Privacy Policy.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            <button
              onClick={handleApprove}
              disabled={loading}
              className="px-6 py-4 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : '✅ Approve & Grant Consent'}
            </button>
            
            <button
              onClick={handleDeny}
              disabled={loading}
              className="px-6 py-4 rounded-lg bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : '🚫 Deny Access'}
            </button>
          </div>

          <p className="text-xs text-slate-500 text-center mt-6">
            Questions? Contact us at <a href="mailto:support@whipt.app" className="text-purple-400 hover:underline">support@whipt.app</a> or call (555) 123-4567
          </p>
        </div>
      </div>
    </div>
  );
};

export default ParentalConsentPage;
